import { useQueryClient } from '@tanstack/react-query';
import { toast } from './use-toast';
import { queryKeys } from '../lib/query-key-factory';
import { useCacheConfig } from '../contexts/cache-config-context';
import { EntityType, RefreshOptions, CacheMetrics } from '../types/cache-types';

export function useCacheControl() {
  let queryClient;

  const { getCacheTime } = useCacheConfig();
  // Temporary admin check - in a real app this should come from auth context
  const isAdmin = true;
  try {
    queryClient = useQueryClient();
    // Anche useCacheConfig potrebbe fallire se non siamo in un CacheConfigProvider
    try {
      const cacheConfig = useCacheConfig();
    } catch (error) {
      console.error('CacheConfig non disponibile:', error);
    }
  } catch (error) {
    console.error('QueryClient non disponibile:', error);
    return {
      getCacheMetrics: () => ({
        totalQueries: 0,
        activeQueries: 0,
        staleQueries: 0,
        queryTypes: {},
        recentlyUpdated: 0,
      }),
      invalidateEntityCache: () => {
        toast({
          title: 'Errore',
          description: 'QueryClient non disponibile',
          status: 'error',
        });
      },
      refreshEntityCache: () => {
        toast({
          title: 'Errore',
          description: 'QueryClient non disponibile',
          status: 'error',
        });
      },
      resetEntireCache: () => {
        toast({
          title: 'Errore',
          description: 'QueryClient non disponibile',
          status: 'error',
        });
      },
      isAdmin,
    };
  }
  
  /**
   * Ottiene le metriche sulla cache attuale
   */
  const getCacheMetrics = (): CacheMetrics => {
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();
    
    const now = Date.now();
    const recentlyUpdated = queries.filter(q => 
      (now - q.state.dataUpdatedAt) < 5 * 60 * 1000 // ultimi 5 minuti
    ).length;
    
    // Raggruppa le query per tipo (primo elemento della query key)
    const queryTypes: Record<string, number> = {};
    queries.forEach(query => {
      const keyType = Array.isArray(query.queryKey) && query.queryKey.length > 0
        ? String(query.queryKey[0])
        : 'unknown';
      
      queryTypes[keyType] = (queryTypes[keyType] || 0) + 1;
    });
    
    return {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.isActive()).length,
      staleQueries: queries.filter(q => q.isStale()).length,
      queryTypes,
      recentlyUpdated,
    };
  };
  
  /**
   * Invalida la cache per un tipo di entità specifico
   */
  const invalidateEntityCache = async (entity: EntityType) => {
    if (!isAdmin) {
      toast({
        title: 'Accesso negato',
        description: 'Solo gli amministratori possono invalidare la cache',
        status: 'error',
      });
      return;
    }
    
    try {
      await queryClient.invalidateQueries({ queryKey: (queryKeys as any)[entity]?.all || [entity] });
      
      toast({
        title: 'Cache invalidata',
        description: `La cache per ${entity} è stata invalidata con successo`,
        status: 'success',
      });
    } catch (error) {
      console.error(`Errore durante l'invalidazione della cache per ${entity}:`, error);
      
      toast({
        title: 'Errore',
        description: `Impossibile invalidare la cache per ${entity}`,
        status: 'error',
      });
    }
  };
  
  /**
   * Ricarica immediatamente i dati per un tipo di entità specifico
   */
  const refreshEntityCache = async (entity: EntityType, options?: RefreshOptions) => {
    if (!isAdmin) {
      toast({
        title: 'Accesso negato',
        description: 'Solo gli amministratori possono ricaricare la cache',
        status: 'error',
      });
      return;
    }
    
    try {
      await queryClient.refetchQueries({
        queryKey: (queryKeys as any)[entity]?.all || [entity],
        exact: options?.exact,
        type: options?.refetchType,
        // Removed invalid property 'throwOnError'
      });
      
      toast({
        title: 'Dati ricaricati',
        description: `I dati per ${entity} sono stati ricaricati con successo`,
        status: 'success',
      });
    } catch (error) {
      console.error(`Errore durante il refresh della cache per ${entity}:`, error);
      
      toast({
        title: 'Errore',
        description: `Impossibile ricaricare i dati per ${entity}`,
        status: 'error',
      });
    }
  };
  
  /**
   * Resetta completamente la cache per tutte le entità
   */
  const resetEntireCache = () => {
    if (!isAdmin) {
      toast({
        title: 'Accesso negato',
        description: 'Solo gli amministratori possono resettare la cache',
        status: 'error',
      });
      return;
    }
    
    try {
      queryClient.clear();
      
      toast({
        title: 'Cache resettata',
        description: 'Tutta la cache è stata resettata con successo',
        status: 'success',
      });
    } catch (error) {
      console.error('Errore durante il reset della cache:', error);
      
      toast({
        title: 'Errore',
        description: 'Impossibile resettare la cache',
        status: 'error',
      });
    }
  };
  
  return {
    getCacheMetrics,
    invalidateEntityCache,
    refreshEntityCache,
    resetEntireCache,
    isAdmin,
  };
}